import { IAccountTag, IImpersonationContext, ITagCategory } from '@ViewModels';
import { createContext, FC, useContext, useMemo, useState } from 'react';
import { useTagsQuery } from '../../../../../queries';
import { useGetTagCategoriesQuery } from '../../../../../queries/TagCategory/useGetTagCategoriesQuery';
import { useGetTagCategoryQuery } from '../../../../../queries/TagCategory/useGetTagCategoryQuery';
import { useTagsSearchContext } from './TagsSearchContext';

interface TagsDataContext {
	categories: {
		data: ITagCategory[] | null;
		hasNextPage: boolean;
		isFetching: boolean;
		isFetchingNextPage: boolean;
		fetchNextPage: () => void;
		totalCount: number;
	};
	isFetching: boolean;
	selectedCategory: ITagCategory | null;
	selectedTag: IAccountTag | null;
	setSelectedCategory: (value: ITagCategory | null) => void;
	setSelectedTag: (value: IAccountTag | null) => void;
	tags: {
		data: IAccountTag[];
		hasNextPage: boolean;
		isFetching: boolean;
		isFetchingNextPage: boolean;
		fetchNextPage: () => void;
		totalCount: number;
	};
}

interface TagsDataContextProviderProps {
	impersonationContext?: IImpersonationContext;
}

const TagsDataContext = createContext<TagsDataContext>({
	categories: {
		data: [],
		hasNextPage: false,
		isFetching: false,
		isFetchingNextPage: false,
		fetchNextPage: () => {
			throw new Error('Function not implmented');
		},
		totalCount: 0,
	},
	isFetching: false,
	selectedCategory: undefined,
	selectedTag: undefined,
	setSelectedCategory: () => {
		throw new Error('Function not implemented.');
	},
	setSelectedTag: () => {
		throw new Error('Function not implemented.');
	},
	tags: {
		data: [],
		hasNextPage: false,
		isFetching: false,
		isFetchingNextPage: false,
		fetchNextPage: () => {
			throw new Error('Function not implmented');
		},
		totalCount: 0,
	},
});

export const TagsDataContextProvider: FC<React.PropsWithChildren<TagsDataContextProviderProps>> = ({
	children,
	impersonationContext,
}) => {
	const [selectedCategory, setSelectedCategory] = useState<ITagCategory | null>();
	const [selectedTag, setSelectedTag] = useState<IAccountTag | null>();
	const { sort, searchTerm } = useTagsSearchContext();

	const tagsQuery = useTagsQuery({
		expandTagAlert: true,
		expandTagCategory: true,
		enabled: !selectedCategory,
		impersonationContext,
		sortDescriptor: sort,
		query: searchTerm,
	});

	const categoriesQuery = useGetTagCategoriesQuery({
		enabled: !selectedCategory,
		impersonationContext,
		sort,
		query: searchTerm,
	});

	const tagsByCategoryQuery = useGetTagCategoryQuery({
		enabled: !!selectedCategory,
		categoryId: selectedCategory?.id,
		expandTags: true,
	});

	const categories = useMemo(() => {
		return categoriesQuery.data?.pages?.flatMap(page => page.values) ?? [];
	}, [categoriesQuery.data?.pages]);

	const tags = useMemo(() => {
		const value = selectedCategory
			? tagsByCategoryQuery.data?.tags
			: tagsQuery.data?.pages?.flatMap(page => page.values);
		return value ?? [];
	}, [selectedCategory, tagsByCategoryQuery.data?.tags, tagsQuery.data?.pages]);

	return (
		<TagsDataContext.Provider
			value={{
				categories: {
					data: categories,
					hasNextPage: categoriesQuery.hasNextPage,
					isFetching: categoriesQuery.isFetching,
					isFetchingNextPage: categoriesQuery.isFetchingNextPage,
					fetchNextPage: categoriesQuery.fetchNextPage,
					totalCount: categoriesQuery?.data?.pages?.[0]?.totalCount ?? 0,
				},
				isFetching: selectedCategory
					? tagsByCategoryQuery.isFetching
					: categoriesQuery.isFetching || tagsQuery.isFetching,
				selectedTag,
				setSelectedTag,
				selectedCategory,
				setSelectedCategory,
				tags: {
					data: tags,
					hasNextPage: selectedCategory ? false : tagsQuery.hasNextPage,
					isFetching: selectedCategory ? tagsByCategoryQuery.isFetching : tagsQuery.isFetching,
					isFetchingNextPage: selectedCategory ? false : tagsQuery.isFetchingNextPage,
					fetchNextPage: selectedCategory ? () => null : tagsQuery.fetchNextPage,
					totalCount: selectedCategory
						? (tagsByCategoryQuery?.data?.tags?.length ?? 0)
						: (tagsQuery.data?.pages?.[0]?.totalCount ?? 0),
				},
			}}
		>
			{children}
		</TagsDataContext.Provider>
	);
};

export const useTagsDataContext = () => {
	return useContext<TagsDataContext>(TagsDataContext);
};
